'use client';
import React, { useState } from 'react';
import { Mic, Play, CheckCircle, Clock, Flame } from 'lucide-react';
import { toast } from 'sonner';

export default function TodayMissionHero() {
  const [started, setStarted] = useState(false);

  const handleStart = () => {
    setStarted(true);
    toast?.success("Today's practice started! Speak for 5 minutes about your day.", {
      duration: 4000,
    });
  };

  return (
    <div className="rounded-3xl overflow-hidden gradient-hero">
      <div className="p-6 lg:p-8">
        {/* Greeting */}
        <div className="flex items-start justify-between mb-6">
          <div>
            <p className="text-white/60 text-sm font-medium mb-1">
              Saturday, 22 Aug 2026 · 8:34 AM
            </p>
            <h1 className="text-2xl lg:text-3xl font-extrabold text-white">
              Good Morning, Priya! 👋
            </h1>
          </div>
          {/* Streak badge */}
          <div className="flex-shrink-0 flex flex-col items-center px-4 py-3 rounded-2xl bg-white/10 border border-white/20 streak-glow">
            <span className="text-2xl streak-bounce">🔥</span>
            <span className="text-xl font-extrabold text-white font-tabular leading-none">17</span>
            <span className="text-xs text-white/60 font-medium">day streak</span>
          </div>
        </div>

        {/* Mission card */}
        <div className="bg-white/10 border border-white/20 rounded-2xl p-5 lg:p-6">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-2xl gradient-green flex items-center justify-center flex-shrink-0 shadow-glow-green">
              <Mic size={22} className="text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <span className="badge-green text-xs">DAY 17 MISSION</span>
                <span className="text-xs text-white/50 font-medium">Today's Speaking Task</span>
              </div>
              <h2 className="text-lg lg:text-xl font-extrabold text-white mb-1.5">
                Talk About Your Daily Routine
              </h2>
              <p className="text-sm text-white/70 leading-relaxed mb-4">
                Speak in English for{' '}
                <span className="text-primary-light font-bold">5 minutes</span> about your typical
                day — what you do in the morning, afternoon, and evening. Use present tense and
                connecting words.
              </p>

              {/* Meta */}
              <div className="flex flex-wrap items-center gap-4 mb-5">
                <div className="flex items-center gap-1.5 text-white/60">
                  <Clock size={14} />
                  <span className="text-xs font-medium">5 min speaking task</span>
                </div>
                <div className="flex items-center gap-1.5 text-white/60">
                  <Flame size={14} className="text-orange-400" />
                  <span className="text-xs font-medium">+50 XP on completion</span>
                </div>
                <div className="flex items-center gap-1.5 text-white/60">
                  <CheckCircle size={14} className="text-primary-light" />
                  <span className="text-xs font-medium">Speakathon Mission 4</span>
                </div>
              </div>

              {/* CTA */}
              {!started ? (
                <button
                  onClick={handleStart}
                  className="inline-flex items-center gap-2.5 px-6 py-3 rounded-2xl font-bold text-sm text-white btn-press shadow-glow-green"
                  style={{ background: 'var(--primary)' }}
                >
                  <Play size={18} />
                  Start Today's Practice
                </button>
              ) : (
                <div className="inline-flex items-center gap-2.5 px-6 py-3 rounded-2xl font-bold text-sm text-white bg-white/20 border border-white/30">
                  <span className="w-2 h-2 rounded-full bg-primary-light animate-pulse" />
                  Recording in Progress...
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Quick stats row */}
        <div className="grid grid-cols-3 lg:grid-cols-3 gap-3 mt-4">
          {[
            { label: 'Course Progress', value: '42%', sub: '53/125 lessons' },
            { label: 'Speakathon', value: 'Mission 4', sub: 'Vocab Booster' },
            { label: 'Speaking Time', value: '8h 35m', sub: 'Total logged' },
          ]?.map((stat) => (
            <div key={`hero-stat-${stat?.label}`} className="bg-white/10 border border-white/15 rounded-xl p-3 text-center">
              <p className="text-base lg:text-lg font-extrabold text-white font-tabular leading-tight">{stat?.value}</p>
              <p className="text-xs text-white/50 font-medium mt-0.5 leading-tight">{stat?.sub}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}